import React, { useState } from "react";
import BaseModal from "./BaseModal";
import { useAuth } from "./AuthProvider";

type View = "signin" | "signup";

interface AuthModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  initialView?: View;
}

const AuthModal: React.FC<AuthModalProps> = ({
  onClose,
  onSuccess,
  initialView = "signin",
}) => {
  const [view, setView] = useState<View>(initialView);

  return (
    <BaseModal onClose={onClose} size={view === "signup" ? "large" : "default"}>
      {view === "signin" ? (
        <SignInForm
          onClose={onClose}
          onSuccess={onSuccess}
          onSwitchToRegister={() => setView("signup")}
        />
      ) : (
        <SignUpForm
          onClose={onClose}
          onSuccess={onSuccess}
          onSwitchToSignIn={() => setView("signin")}
        />
      )}
    </BaseModal>
  );
};

type SignInView = "signin" | "forgot-request" | "forgot-confirm";

interface SignInFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToRegister: () => void;
}

const SignInForm: React.FC<SignInFormProps> = ({
  onClose,
  onSuccess,
  onSwitchToRegister,
}) => {
  const { signIn, resetPassword, confirmResetPassword, startHostedLogin, hostedUIEnabled } = useAuth();
  const [view, setView] = useState<SignInView>("signin");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [forgotData, setForgotData] = useState({ email: "", code: "", newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next: { [key: string]: string } = {};
    if (!formData.email) next.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) next.email = "Enter a valid email";
    if (!formData.password) next.password = "Password is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      await signIn(formData.email, formData.password);
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      const message =
        err?.name === "NotAuthorizedException"
          ? "Incorrect email or password"
          : err?.message || "Sign in failed. Try again.";
      setErrors({ general: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!hostedUIEnabled) return;
    setIsLoading(true);
    try {
      await startHostedLogin();
    } catch (error: unknown) {
      setErrors({ general: (error as Error)?.message || "Google sign-in failed" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = forgotData.email.trim();
    if (!email) {
      setErrors({ general: "Enter your email address" });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ general: "Enter a valid email address" });
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      await resetPassword(email);
      setView("forgot-confirm");
      setErrors({});
    } catch (error: unknown) {
      setErrors({ general: (error as Error)?.message || "Could not send reset code. Try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (forgotData.newPassword.length < 8) {
      setErrors({ general: "Password must be at least 8 characters" });
      return;
    }
    if (forgotData.newPassword !== forgotData.confirmPassword) {
      setErrors({ general: "Passwords do not match" });
      return;
    }
    if (!forgotData.code.trim()) {
      setErrors({ general: "Enter the code from your email" });
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      await confirmResetPassword(forgotData.email.trim(), forgotData.code, forgotData.newPassword);
      setView("signin");
      setForgotData((prev) => ({ ...prev, code: "", newPassword: "", confirmPassword: "" }));
      setErrors({});
      setSuccessMessage("Password reset. You can sign in with your new password.");
    } catch (error: unknown) {
      setErrors({ general: (error as Error)?.message || "Reset failed. Check the code and try again." });
    } finally {
      setIsLoading(false);
    }
  };

  if (view === "forgot-request") {
    return (
      <>
        <div className="modal-header">
          <h2>Forgot password</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <span>&times;</span>
          </button>
        </div>
        <form className="modal-form" onSubmit={handleForgotRequest}>
          {errors.general && (
            <div className="error-message general-error">{errors.general}</div>
          )}
          <div className="form-group">
            <label htmlFor="auth-forgot-email">Email</label>
            <input
              id="auth-forgot-email"
              type="email"
              value={forgotData.email}
              onChange={(e) => setForgotData((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="you@example.com"
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
          <button
            type="submit"
            className="btn-modal btn-modal-primary"
            disabled={isLoading}
          >
            {isLoading ? "Sending…" : "Send reset code"}
          </button>
        </form>
        <div className="modal-footer">
          <p>
            <button
              type="button"
              className="link-button"
              onClick={() => { setView("signin"); setErrors({}); }}
              disabled={isLoading}
            >
              Back to sign in
            </button>
          </p>
        </div>
      </>
    );
  }

  if (view === "forgot-confirm") {
    return (
      <>
        <div className="modal-header">
          <h2>Reset password</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <span>&times;</span>
          </button>
        </div>
        <form className="modal-form" onSubmit={handleForgotConfirm}>
          {errors.general && (
            <div className="error-message general-error">{errors.general}</div>
          )}
          <div className="form-group">
            <label htmlFor="auth-forgot-code">Code from email</label>
            <input
              id="auth-forgot-code"
              type="text"
              value={forgotData.code}
              onChange={(e) => setForgotData((prev) => ({ ...prev, code: e.target.value }))}
              placeholder="Enter code"
              disabled={isLoading}
              autoComplete="one-time-code"
            />
          </div>
          <div className="form-group">
            <label htmlFor="auth-forgot-new-password">New password</label>
            <input
              id="auth-forgot-new-password"
              type="password"
              value={forgotData.newPassword}
              onChange={(e) => setForgotData((prev) => ({ ...prev, newPassword: e.target.value }))}
              placeholder="At least 8 characters"
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="auth-forgot-confirm-password">Confirm new password</label>
            <input
              id="auth-forgot-confirm-password"
              type="password"
              value={forgotData.confirmPassword}
              onChange={(e) => setForgotData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Repeat password"
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            className="btn-modal btn-modal-primary"
            disabled={isLoading}
          >
            {isLoading ? "Resetting…" : "Reset password"}
          </button>
        </form>
        <div className="modal-footer">
          <p>
            <button
              type="button"
              className="link-button"
              onClick={() => { setView("forgot-request"); setErrors({}); }}
              disabled={isLoading}
            >
              Use a different email
            </button>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="modal-header">
        <h2>Sign in</h2>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          <span>&times;</span>
        </button>
      </div>

      <form className="modal-form" onSubmit={handleSubmit}>
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}
        {successMessage && (
          <div className="general-success">{successMessage}</div>
        )}

        {hostedUIEnabled && (
          <>
            <div className="social-login">
              <button
                type="button"
                className="btn-google"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                Sign in with Google
              </button>
            </div>
            <div className="modal-divider">or with email</div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="auth-login-email">Email</label>
          <input
            id="auth-login-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            disabled={isLoading}
            className={errors.email ? "error" : ""}
            autoComplete="email"
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="auth-login-password">Password</label>
          <input
            id="auth-login-password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••"
            disabled={isLoading}
            className={errors.password ? "error" : ""}
            autoComplete="current-password"
          />
          {errors.password && <span className="error-message">{errors.password}</span>}
        </div>

        <div className="form-group" style={{ marginTop: "-0.5rem" }}>
          <button
            type="button"
            className="link-button"
            onClick={() => setView("forgot-request")}
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          className="btn-modal btn-modal-primary"
          disabled={isLoading}
        >
          {isLoading ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <div className="modal-footer">
        <p>
          Don&apos;t have an account?{" "}
          <button
            type="button"
            className="link-button"
            onClick={onSwitchToRegister}
            disabled={isLoading}
          >
            Sign up
          </button>
        </p>
      </div>
    </>
  );
};

interface SignUpFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToSignIn: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  onClose,
  onSuccess,
  onSwitchToSignIn,
}) => {
  const { signUp, confirmSignUp, startHostedLogin, hostedUIEnabled } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [code, setCode] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next: { [key: string]: string } = {};
    if (!formData.email) next.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) next.email = "Enter a valid email";
    if (!formData.password) next.password = "Password is required";
    else if (formData.password.length < 8) next.password = "At least 8 characters";
    if (formData.password !== formData.confirmPassword)
      next.confirmPassword = "Passwords do not match";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showConfirm) {
      if (!validate()) return;
      setIsLoading(true);
      try {
        await signUp(formData.email, formData.password);
        setShowConfirm(true);
      } catch (error: unknown) {
        const err = error as { name?: string; message?: string };
        const message =
          err?.name === "UsernameExistsException"
            ? "An account with this email already exists"
            : err?.message || "Registration failed. Try again.";
        setErrors({ general: message });
      } finally {
        setIsLoading(false);
      }
      return;
    }
    if (!code.trim()) {
      setErrors({ general: "Enter the code from your email" });
      return;
    }
    setIsLoading(true);
    try {
      await confirmSignUp(formData.email, code);
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      setErrors({ general: err?.message || "Confirmation failed. Try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    if (!hostedUIEnabled) return;
    setIsLoading(true);
    try {
      await startHostedLogin();
    } catch (error: unknown) {
      setErrors({ general: (error as Error)?.message || "Google sign-up failed" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="modal-header">
        <h2>Create account</h2>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          <span>&times;</span>
        </button>
      </div>

      <form className="modal-form" onSubmit={handleSubmit}>
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        {hostedUIEnabled && !showConfirm && (
          <>
            <div className="social-login">
              <button
                type="button"
                className="btn-google"
                onClick={handleGoogleSignUp}
                disabled={isLoading}
              >
                Sign up with Google
              </button>
            </div>
            <div className="modal-divider">or with email</div>
          </>
        )}

        {!showConfirm && (
          <>
            <div className="form-group">
              <label htmlFor="auth-reg-email">Email</label>
              <input
                id="auth-reg-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                disabled={isLoading}
                className={errors.email ? "error" : ""}
                autoComplete="email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="auth-reg-password">Password</label>
              <input
                id="auth-reg-password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 8 characters"
                disabled={isLoading}
                className={errors.password ? "error" : ""}
                autoComplete="new-password"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="auth-reg-confirm">Confirm password</label>
              <input
                id="auth-reg-confirm"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat password"
                disabled={isLoading}
                className={errors.confirmPassword ? "error" : ""}
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>
            <button
              type="submit"
              className="btn-modal btn-modal-primary"
              disabled={isLoading}
            >
              {isLoading ? "Creating account…" : "Create account"}
            </button>
          </>
        )}

        {showConfirm && (
          <>
            <div className="form-group">
              <label htmlFor="auth-reg-code">Verification code</label>
              <input
                id="auth-reg-code"
                type="text"
                name="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Code from your email"
                disabled={isLoading}
                autoComplete="one-time-code"
              />
            </div>
            <button
              type="submit"
              className="btn-modal btn-modal-primary"
              disabled={isLoading}
            >
              {isLoading ? "Confirming…" : "Confirm"}
            </button>
          </>
        )}
      </form>

      <div className="modal-footer">
        <p>
          Already have an account?{" "}
          <button
            type="button"
            className="link-button"
            onClick={onSwitchToSignIn}
            disabled={isLoading}
          >
            Sign in
          </button>
        </p>
      </div>
    </>
  );
};

export default AuthModal;
